import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, AfterViewChecked, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { filter, Subscription } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

export interface MobileTopTab {
  key: string;
  label: string;
  queryParams?: Params;
  /** The data-tour slug a guided tour points at. */
  tour?: string;
}

@Component({
  selector: 'app-mobile-top-bar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './mobile-top-bar.component.html',
  styleUrls: ['./mobile-top-bar.component.css'],
})
export class MobileTopBarComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() showBurger = true;
  @Input() showLogout = true;
  @Output() navOpen = new EventEmitter<void>();

  pageTitle = 'AIVOR';
  tabs: MobileTopTab[] = [];
  activeTabKey = '';
  userName = '';

  private subs = new Subscription();
  private lastMeasuredHeight = 0;
  private resizeListener: (() => void) | null = null;

  constructor(
    public authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private elRef: ElementRef,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
        this.recomputeFromRoute();
      })
    );

    this.subs.add(
      this.route.queryParams.subscribe(() => {
        this.recomputeFromRoute();
      })
    );

    this.recomputeFromRoute();

    // Decode user name from JWT (same pattern as config/dashboard components)
    const JWT = localStorage.getItem('authToken');
    if (JWT) {
      try {
        const decoded: any = jwtDecode(JWT);
        this.userName = decoded.FullName || decoded.email || '';
      } catch { }
    }

    // Listen for resize/orientation changes to recalculate offset
    this.ngZone.runOutsideAngular(() => {
      this.resizeListener = () => this.updateTopBarOffset();
      window.addEventListener('resize', this.resizeListener);
      window.addEventListener('orientationchange', this.resizeListener);
    });
  }

  ngAfterViewChecked(): void {
    this.updateTopBarOffset();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      window.removeEventListener('orientationchange', this.resizeListener);
    }
    document.documentElement.style.setProperty('--mobile-topbar-offset', '0px');
  }

  private updateTopBarOffset(): void {
    const el = this.elRef.nativeElement as HTMLElement;
    const height = el.offsetHeight;
    if (height !== this.lastMeasuredHeight) {
      this.lastMeasuredHeight = height;
      document.documentElement.style.setProperty('--mobile-topbar-offset', `${height}px`);
    }
  }

  openNav(): void {
    this.navOpen.emit();
  }

  openNavTouch(event: TouchEvent): void {
    event.preventDefault();
    this.markTouchHandled();
    this.openNav();
  }

  private touchHandled = false;
  private markTouchHandled(): void {
    this.touchHandled = true;
    setTimeout(() => (this.touchHandled = false), 400);
  }
  selectTabClick(tab: MobileTopTab): void {
    if (this.touchHandled) return;
    this.selectTab(tab);
  }
  selectTabTouch(tab: MobileTopTab, event: TouchEvent): void {
    event.preventDefault();
    this.markTouchHandled();
    this.selectTab(tab);
  }

  selectTab(tab: MobileTopTab): void {
    const qp = tab.queryParams ?? {};
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: qp,
      queryParamsHandling: '',
    });
  }

  private recomputeFromRoute(): void {
    const url = this.router.url;
    const qp = this.route.snapshot.queryParams;

    if (url.startsWith('/calc')) {
      this.pageTitle = 'Cost Calculator';
      this.tabs = [
        { key: 'calculator', label: 'Calculator', queryParams: {} },
        { key: 'summary', label: 'Summary', queryParams: { view: 'summary' } },
        { key: 'graphs', label: 'Graphs', queryParams: { view: 'graphs' } },
        { key: 'aivor', label: 'AI Insights', queryParams: { view: 'aivor' } },
      ];

      const view = qp['view'];
      this.activeTabKey = view === 'summary' ? 'summary' : view === 'graphs' ? 'graphs' : view === 'aivor' ? 'aivor' : 'calculator';
      return;
    }

    if (url.startsWith('/dashboard')) {
      this.pageTitle = 'Dashboard';
      this.tabs = [
        { key: 'overview', label: 'Overview',   queryParams: { view: 'overview' } },
        { key: 'tables',   label: 'Breakdown', queryParams: { view: 'tables' } },
        { key: 'charts',   label: 'Trends',    queryParams: { view: 'charts' } },
      ];
      this.activeTabKey = qp['view'] ?? 'overview';
      return;
    }

    if (url.startsWith('/inventory')) {
      // Raw Materials and Products are separate routed pages now, but each
      // still has its own Overview/Items/Charts/Import sub-views (same
      // mechanism as before the split — a `view` query param on whichever
      // route we're on), so the tab strip is per-route, not shared.
      this.pageTitle = url.startsWith('/inventory/raw-materials') ? 'Raw Materials' : 'Products';
      this.tabs = [
        { key: 'overview', label: 'Overview', queryParams: {} },
        { key: 'tables', label: 'Items', queryParams: { view: 'tables' } },
        { key: 'charts', label: 'Charts', queryParams: { view: 'charts' } },
        { key: 'import', label: 'Import', queryParams: { view: 'import' } },
      ];
      this.activeTabKey = qp['view'] ?? 'overview';
      return;
    }

    if (url.startsWith('/manufacturing')) {
      this.pageTitle = 'Manufacturing';
      this.tabs = [
        { key: 'bom', label: 'BOM', queryParams: { view: 'bom' } },
        { key: 'orders', label: 'Orders', queryParams: { view: 'orders' } },
        { key: 'workflow', label: 'Workflow', queryParams: { view: 'workflow' } },
        { key: 'products', label: 'Products', queryParams: { view: 'products' } },
      ];
      this.activeTabKey = qp['view'] ?? 'bom';
      return;
    }

    if (url.startsWith('/costing')) {
      this.pageTitle = 'Budgeting';
      this.tabs = [
        { key: 'overview', label: 'Overview',    queryParams: {} },
        { key: 'budgets',  label: 'Budgets',     queryParams: { view: 'budgets' } },
        { key: 'ai',       label: 'AI Insights', queryParams: { view: 'ai' } },
      ];
      const v = qp['view'];
      this.activeTabKey = v === 'budgets' ? 'budgets' : v === 'ai' ? 'ai' : 'overview';
      return;
    }

    if (url.startsWith('/business-details')) {
      this.pageTitle = 'Business Details';
      this.tabs = [
        { key: 'details',   label: 'Details',    queryParams: { tab: 'details' } },
        { key: 'schedule',  label: 'Schedule',   queryParams: { tab: 'schedule' } },
        { key: 'expenses',  label: 'Expenses',   queryParams: { tab: 'expenses' } },
        { key: 'employees', label: 'Employees',  queryParams: { tab: 'employees' } },
        { key: 'salaries',  label: 'Salaries',   queryParams: { tab: 'salaries' } },
      ];
      this.activeTabKey = qp['tab'] ?? 'details';
      return;
    }

    if (url.startsWith('/config')) {
      this.pageTitle = 'System Administration';
      this.tabs = [
        { key: 'users', label: 'Users', queryParams: { tab: 'users' } },
        { key: 'roles', label: 'Roles', queryParams: { tab: 'roles' } },
        { key: 'categories', label: 'Categories', queryParams: { tab: 'categories' } },
        { key: 'suppliers', label: 'Suppliers', queryParams: { tab: 'suppliers' } },
        { key: 'subscription', label: 'Subscription', queryParams: { tab: 'subscription' } },
      ];
      this.activeTabKey = qp['tab'] ?? 'users';
      return;
    }

    if (url.startsWith('/reports')) {
      this.pageTitle = 'Reports';
      this.tabs = [];
      this.activeTabKey = '';
      return;
    }

    // Before the general Sage branch below, which would match this URL too.
    if (url.startsWith('/sage-integration/console')) {
      this.pageTitle = 'Sage Integration';
      this.tabs = [
        { key: 'sync',      label: 'Dashboard',    queryParams: { tab: 'sync' } },
        { key: 'matches',   label: 'Fix matches',  queryParams: { tab: 'matches' },   tour: 'tab-matches' },
        { key: 'runs',      label: 'Runs',         queryParams: { tab: 'runs' },      tour: 'tab-runs' },
        { key: 'conflicts', label: 'Conflicts',    queryParams: { tab: 'conflicts' }, tour: 'tab-conflicts' },
        { key: 'groups',    label: 'Groups',       queryParams: { tab: 'groups' },    tour: 'tab-groups' },
      ];
      const tab = qp['tab'];
      // Review, Errors and Stock are drill-downs from the dashboard, so the Dashboard tab stays lit.
      this.activeTabKey =
        tab === 'runs' || tab === 'matches' || tab === 'conflicts' || tab === 'groups' ? tab : 'sync';
      return;
    }

    if (url.startsWith('/sage-integration') || url.startsWith('/settings/integrations')) {
      this.pageTitle = 'Sage Integration';
      this.tabs = [];
      this.activeTabKey = '';
      return;
    }

    this.pageTitle = 'AIVOR';
    this.tabs = [];
    this.activeTabKey = '';
  }
}
